import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Shield, Plus, Trash2, Moon, Users, Dumbbell, BookOpen, Heart, Coffee, Music, X, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import ClashReviewPanel from '@/components/ClashReviewPanel';
import ProUpgradeCard from '@/components/ProUpgradeCard';
import ProBadge from '@/components/ProBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { shield: Shield, moon: Moon, users: Users, dumbbell: Dumbbell, book: BookOpen, heart: Heart, coffee: Coffee, music: Music };
const COLORS = ['#00D9FF','#a78bfa','#22c55e','#f97316','#ec4899','#eab308','#3b82f6','#ef4444'];
const DAYS_ALL = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

const PRESETS = [
  { label: 'Sleep', icon: 'moon', start_time: '22:30', end_time: '07:00', days: ['everyday'], color: '#a78bfa' },
  { label: 'Morning Workout', icon: 'dumbbell', start_time: '06:00', end_time: '07:30', days: ['monday','wednesday','friday'], color: '#22c55e' },
  { label: 'Family Dinner', icon: 'users', start_time: '18:30', end_time: '20:00', days: ['everyday'], color: '#f97316' },
  { label: 'Deep Work Block', icon: 'book', start_time: '09:00', end_time: '11:00', days: ['monday','tuesday','wednesday','thursday','friday'], color: '#00D9FF' },
  { label: 'No-Screen Wind Down', icon: 'moon', start_time: '21:00', end_time: '22:30', days: ['everyday'], color: '#ec4899' },
];

const SHORT_LABELS = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };
const formatDays = (days = []) => {
  const norm = days.map(d => (d || '').toLowerCase());
  if (norm.some(d => d.startsWith('every')) || norm.length === 7) return 'Every day';
  return norm.map(d => SHORT_LABELS[d.slice(0, 3)]).filter(Boolean).join(', ') || 'No days set';
};

const NNCard = ({ item, onDelete, onEdit }) => {
  const Icon = ICONS[item.icon] || Shield;
  const daysLabel = formatDays(item.days);
  return (
    <div className="glass-card p-4 flex items-center gap-4 group" data-testid={`nn-card-${item.id}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${item.color}18`, border: `1px solid ${item.color}35` }}>
        <Icon size={18} style={{ color: item.color }}/>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">{item.label}</p>
        <p className="text-xs text-gray-400 mt-0.5" data-testid={`nn-card-time-${item.id}`}>{item.start_time} – {item.end_time} · {daysLabel}</p>
      </div>
      <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: item.color }}/>
      <button onClick={() => onEdit(item)} data-testid={`nn-edit-btn-${item.id}`} title="Edit time"
        className="p-1.5 rounded-lg text-gray-500 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors">
        <Pencil size={14}/>
      </button>
      <button onClick={() => onDelete(item.id)} data-testid={`nn-delete-btn-${item.id}`}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-400/10">
        <Trash2 size={14}/>
      </button>
    </div>
  );
};

const EMPTY_FORM = { label: '', icon: 'shield', start_time: '22:00', end_time: '07:00', days: ['everyday'], color: '#00D9FF' };

const NonNegotiables = () => {
  const { auth } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [clashes, setClashes] = useState(null);
  const [proLock, setProLock] = useState(null);
  const formRef = React.useRef(null);

  const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); };

  const handleEdit = (item) => {
    const { label, icon, start_time, end_time, days, color } = item;
    setForm({ label, icon: icon || 'shield', start_time, end_time, days: days?.length ? days : ['everyday'], color: color || '#00D9FF' });
    setEditingId(item.id);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/non-negotiables`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setItems(res.data || []);
    } catch (e) { if (e.response?.status === 402) setProLock(e.response.data.detail); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleDay = (day) => {
    if (day === 'everyday') {
      setForm(f => ({ ...f, days: ['everyday'] }));
      return;
    }
    setForm(f => {
      const without = f.days.filter(d => d !== 'everyday' && d !== day);
      if (f.days.includes(day)) return { ...f, days: without };
      const next = [...without, day];
      return { ...f, days: next.length === 7 ? ['everyday'] : next };
    });
  };

  const handlePreset = (p) => { setForm({ ...p }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Give it a name'); return; }
    if (!form.days.length) { toast.error('Select at least one day'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${auth.token}` };
      const tz = -new Date().getTimezoneOffset();
      const res = editingId
        ? await axios.put(`${API}/non-negotiables/${editingId}?tz_offset_minutes=${tz}`, form, { headers })
        : await axios.post(`${API}/non-negotiables?tz_offset_minutes=${tz}`, form, { headers });
      const found = res.data?.clashes || [];
      if (found.length) {
        setClashes(found);
        toast.warning(`"${form.label}" saved — ${found.length} task(s) clash with it`);
      } else {
        toast.success(editingId ? `"${form.label}" updated to ${form.start_time}–${form.end_time} ✅` : `"${form.label}" protected ✅`);
      }
      closeForm();
      load();
    } catch (e) { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/non-negotiables/${id}`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Removed');
    } catch (e) { toast.error('Could not remove'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <Loader2 size={28} className="text-cyan-400 animate-spin"/>
    </div>
  );

  if (proLock) return <ProUpgradeCard feature="Non-Negotiables" message={proLock.message + ' Blocks you already created stay protected.'} />;

  return (
    <div className="min-h-screen pt-20 px-4 pb-16" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#a78bfa,#7c3aed)' }}>
              <Shield size={16} color="#fff"/>
            </div>
            <h1 className="heading-font text-2xl font-bold text-white flex items-center gap-2">Non-Negotiables <ProBadge testId="page-pro-badge-non-negotiables"/></h1>
          </div>
          <p className="text-gray-400 text-sm">Define the time blocks that are sacred to you. SpaceCal.ai will never schedule tasks here.</p>
          {clashes?.length > 0 && (
            <div className="mt-5">
              <ClashReviewPanel clashes={clashes} onKeep={() => setClashes(null)}
                onDone={() => {}} />
            </div>
          )}
          {!items.some(i => /sleep|bed/i.test(i.label)) && (
            <p className="text-xs text-violet-300/80 mt-2" data-testid="nn-default-sleep-note">
              Until you add your own Sleep block, 23:00–07:00 is protected by default (not shown as a block).
            </p>
          )}
        </div>

        {/* Existing items */}
        {items.length > 0 ? (
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Protected Time Blocks</h2>
            <div className="space-y-3">
              {items.map(item => <NNCard key={item.id} item={item} onDelete={handleDelete} onEdit={handleEdit}/>)}
            </div>
          </div>
        ) : (
          <div className="glass-card p-8 text-center mb-8">
            <Shield size={32} className="text-gray-600 mx-auto mb-3"/>
            <p className="text-gray-400 text-sm">No protected blocks yet.</p>
            <p className="text-gray-600 text-xs mt-1">Add your first non-negotiable below.</p>
          </div>
        )}

        {/* Quick presets */}
        {!showForm && (
          <div className="mb-6">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Add</h2>
            <div className="grid grid-cols-1 gap-2">
              {PRESETS.map(p => {
                const Icon = ICONS[p.icon] || Shield;
                const alreadyAdded = items.some(i => i.label === p.label);
                return (
                  <button key={p.label} onClick={() => !alreadyAdded && handlePreset(p)} disabled={alreadyAdded}
                    className="flex items-center gap-3 p-3 rounded-xl text-left transition-all hover:bg-white/5 disabled:opacity-40"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${p.color}18` }}>
                      <Icon size={15} style={{ color: p.color }}/>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{p.label}</p>
                      <p className="text-xs text-gray-500">{p.start_time}–{p.end_time} · {p.days.includes('everyday') ? 'Every day' : p.days.map(d=>DAY_LABELS[d]).join(', ')}</p>
                    </div>
                    {alreadyAdded
                      ? <span className="text-xs text-green-400">✓ Added</span>
                      : <Plus size={14} className="text-gray-500"/>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add custom */}
        {!showForm ? (
          <button onClick={() => setShowForm(true)} data-testid="nn-create-custom-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)' }}>
            <Plus size={16}/> Create Custom Block
          </button>
        ) : (
          <div className="glass-card p-6 space-y-5" ref={formRef} data-testid="nn-form">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white" data-testid="nn-form-title">{editingId ? 'Edit Protected Block' : 'New Protected Block'}</h3>
              <button onClick={closeForm} data-testid="nn-form-close-btn" className="text-gray-500 hover:text-white p-1">
                <X size={16}/>
              </button>
            </div>

            {/* Label */}
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider block mb-2">Name</label>
              <input data-testid="nn-label-input" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Family dinner, Morning workout…"
                className="w-full rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}/>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-2">Start</label>
                <input type="time" data-testid="nn-start-time-input" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                  className="w-full rounded-xl px-4 py-2.5 text-sm text-white outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}/>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-2">End</label>
                <input type="time" data-testid="nn-end-time-input" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                  className="w-full rounded-xl px-4 py-2.5 text-sm text-white outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}/>
              </div>
            </div>

            {/* Days */}
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider block mb-2">Days</label>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => toggleDay('everyday')} data-testid="nn-day-everyday"
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ background: form.days.includes('everyday') ? '#00D9FF' : 'rgba(255,255,255,0.05)',
                    color: form.days.includes('everyday') ? '#0A0E27' : '#9ca3af',
                    border: `1px solid ${form.days.includes('everyday') ? '#00D9FF' : 'rgba(255,255,255,0.1)'}` }}>
                  Every day
                </button>
                {DAYS_ALL.map(d => (
                  <button key={d} onClick={() => toggleDay(d)} data-testid={`nn-day-${d}`}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{ background: (form.days.includes(d) || form.days.includes('everyday')) ? '#00D9FF22' : 'rgba(255,255,255,0.03)',
                      color: (form.days.includes(d) || form.days.includes('everyday')) ? '#00D9FF' : '#9ca3af',
                      border: `1px solid ${(form.days.includes(d) || form.days.includes('everyday')) ? '#00D9FF44' : 'rgba(255,255,255,0.08)'}` }}>
                    {DAY_LABELS[d]}
                  </button>
                ))}
              </div>
            </div>

            {/* Color & Icon */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-2">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                      style={{ background: c, border: form.color === c ? `2px solid white` : '2px solid transparent', outline: form.color === c ? `2px solid ${c}` : 'none', outlineOffset: 2 }}/>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-2">Icon</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(ICONS).map(([key, Icon]) => (
                    <button key={key} onClick={() => setForm(f => ({ ...f, icon: key }))}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                      style={{ background: form.icon === key ? `${form.color}22` : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${form.icon === key ? form.color : 'rgba(255,255,255,0.1)'}` }}>
                      <Icon size={14} style={{ color: form.icon === key ? form.color : '#6b7280' }}/>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={handleSave} disabled={saving} data-testid="nn-save-btn"
              className="w-full teal-btn py-3 text-sm font-semibold flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin"/> : <Shield size={15}/>}
              {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Protect This Time')}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default NonNegotiables;
